import { FC, useCallback, useEffect, useMemo } from 'react';
import styled from '@emotion/styled';
import { useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';
import IcSettingFill from 'src/assets/images/icSettingFill.svg';
import { GUIDE_IMAGES, STTDeniedGuideProps } from 'src/components/AzarOnly/AssistantConsent/speechRecognitionDeniedGuideConstants';
import ButtonBase from 'src/components/Button';
import Image from 'src/components/Image';
import ModalLayoutBase from 'src/components/ModalLayout';
import useIsRtl from 'src/hooks/useIsRtl';
import { closeModalAtom } from 'src/stores/modal/atoms';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { ModalNavButtonType, ModalType } from 'src/types/Modal';
import { isChromeBrowser } from 'src/utils/device/info';
import { pushEvent } from 'src/utils/event-gateway';
const ModalLayout = styled(ModalLayoutBase)`
  background: linear-gradient(165deg, #2effb6 0%, #53fbd7 49%, #9bffe3 100%);
  width: 500px;
`;
const Header = styled.div`
  padding: 20px 24px 8px;
  display: flex;
  gap: 8px;
  align-items: center;
  width: 100%;
  flex-shrink: 0;
`;
const HeaderTitle = styled.p`
  flex: 1;
  font-size: 30px;
  font-weight: 700;
  line-height: 40px;
  color: ${({
  theme
}) => theme.color.content12Fixed};
  margin: 0;

  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  text-overflow: ellipsis;
  word-break: break-word;
`;
const SkipButton = styled.button`
  all: unset;
  cursor: pointer;
  flex-shrink: 0;
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
  color: ${({
  theme
}) => theme.color.content12Fixed};
  opacity: 0.5;
  text-align: end;
  white-space: nowrap;
`;
const ContentBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 8px 24px;
  flex: 1;
  overflow-x: hidden;
  overflow-y: auto;
`;
const DescriptionText = styled.p`
  margin: 0;
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  color: ${({
  theme
}) => theme.color.content12Fixed};
  width: 100%;

  a {
    color: ${({
  theme
}) => theme.color.content12Fixed};
    text-decoration: underline;
    font-weight: 700;
  }
`;
const CardWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 24px;
  border-radius: 24px;
`;
const GuideCardImage = styled(Image)`
  width: 100%;
  max-width: 314px;
  height: auto;
`;
const FooterArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 0 24px 24px;
  width: 100%;
  flex-shrink: 0;
`;
const FooterText = styled.p`
  font-size: 13px;
  font-weight: 400;
  line-height: 16px;
  color: ${({
  theme
}) => theme.color.content12Fixed};
  text-align: center;
`;
const OpenSettingsButton = styled(ButtonBase)`
  flex-direction: row;
  gap: 6px;
`;
const SettingIcon = styled(IcSettingFill)`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
`;

/**
 * macOS Safari 브라우저에서 음성인식 권한이 거부되었을때, OS의 권한 설정 페이지로 이동하는 모달
 */
const STTDeniedGuideForMacSafari: FC<STTDeniedGuideProps> = ({
  referrer,
  onComplete
}) => {
  const {
    t
  } = useTranslation();
  const closeModal = useSetAtom(closeModalAtom);
  const isRtl = useIsRtl();
  useEffect(() => {
    pushEvent({
      eventType: EVENT_TYPE.PROMETHEUS,
      eventName: EVENT_NAME.ANYWHERE__IMPRESSION_STT_SETTINGS_POPUP,
      eventParams: {
        action_category: 'impression',
        tab: 'anywhere',
        page: 'anywhere',
        target: 'stt_settings_popup',
        referrer
      }
    });
  }, [referrer]);
  const closeAndComplete = useCallback(() => {
    closeModal(ModalType.SPEECH_RECOGNITION_DENIED_GUIDE);
    onComplete?.();
  }, [closeModal, onComplete]);
  const handleDone = useCallback(() => {
    pushEvent({
      eventType: EVENT_TYPE.PROMETHEUS,
      eventName: EVENT_NAME.STT_SETTINGS_POPUP__CLICK_DONE,
      eventParams: {
        action_category: 'click',
        tab: 'anywhere',
        page: 'stt_settings_popup',
        target: 'done',
        referrer
      }
    });
    closeAndComplete();
  }, [closeAndComplete, referrer]);
  const handleOpenSettings = useCallback(() => {
    pushEvent({
      eventType: EVENT_TYPE.PROMETHEUS,
      eventName: EVENT_NAME.STT_SETTINGS_POPUP__CLICK_OPEN_SETTINGS,
      eventParams: {
        action_category: 'click',
        tab: 'anywhere',
        page: 'stt_settings_popup',
        target: 'open_settings',
        referrer
      }
    });
    window.open('x-apple.systempreferences:com.apple.preference.security?Privacy_SpeechRecognition', '_self');
    closeAndComplete();
  }, [closeAndComplete, referrer]);
  const isChrome = useMemo(() => isChromeBrowser(), []);
  const dir = isRtl ? 'rtl' : 'ltr';
  const browser = isChrome ? 'chrome' : 'safari';
  return <ModalLayout isDim={false} closeWhenClickOutside={false} closeWithESC={false} navButtonType={ModalNavButtonType.NONE} data-testid='SpeechRecognitionDeniedGuide' data-sentry-element="ModalLayout" data-sentry-component="STTDeniedGuideForMacSafari" data-sentry-source-file="STTDeniedGuideForMacSafari.tsx">
      <Header data-sentry-element="Header" data-sentry-source-file="STTDeniedGuideForMacSafari.tsx">
        <HeaderTitle data-sentry-element="HeaderTitle" data-sentry-source-file="STTDeniedGuideForMacSafari.tsx">{t('azar_assistant.consent_nudge_title')}</HeaderTitle>
        <SkipButton onClick={handleDone} data-testid='SpeechRecognitionDeniedGuide-button-skip' data-sentry-element="SkipButton" data-sentry-source-file="STTDeniedGuideForMacSafari.tsx">
          {t('COMMON__SKIP')}
        </SkipButton>
      </Header>
      <ContentBody data-sentry-element="ContentBody" data-sentry-source-file="STTDeniedGuideForMacSafari.tsx">
        <DescriptionText dangerouslySetInnerHTML={{
        __html: t('azar_assistant.consent_nudge_desc')
      }} data-sentry-element="DescriptionText" data-sentry-source-file="STTDeniedGuideForMacSafari.tsx" />
        <CardWrapper data-sentry-element="CardWrapper" data-sentry-source-file="STTDeniedGuideForMacSafari.tsx">
          <GuideCardImage aria-hidden src={GUIDE_IMAGES.speechRecognitionDesktop[browser][dir]} data-sentry-element="GuideCardImage" data-sentry-source-file="STTDeniedGuideForMacSafari.tsx" />
        </CardWrapper>
      </ContentBody>
      <FooterArea data-sentry-element="FooterArea" data-sentry-source-file="STTDeniedGuideForMacSafari.tsx">
        <FooterText data-sentry-element="FooterText" data-sentry-source-file="STTDeniedGuideForMacSafari.tsx">{t('COMMON__SETTING_CHANGE_ANYTIME')}</FooterText>
        <OpenSettingsButton color='secondary' id='speech-recognition-denied-open-settings' onClick={handleOpenSettings} data-testid='SpeechRecognitionDeniedGuide-button-open-settings' data-sentry-element="OpenSettingsButton" data-sentry-source-file="STTDeniedGuideForMacSafari.tsx">
          <SettingIcon data-sentry-element="SettingIcon" data-sentry-source-file="STTDeniedGuideForMacSafari.tsx" />
          {t('azar_assistant.consent_nudge_button_open_settings')}
        </OpenSettingsButton>
      </FooterArea>
    </ModalLayout>;
};
export default STTDeniedGuideForMacSafari;